/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import StyledUserProfile from "./StyledUserProfile";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { fetchProfile } from "../../store/reducers/home/apiThunk";
import { useDispatch } from "react-redux";
import { ToastNotifyError } from "../../components/CommonComponents/Toast/ToastNotify";
import { ROUTE_PATH } from "../../constants/route";
import { useNavigate } from "react-router-dom";
import { SIZE_OPTIONS } from "../../constants/optionsConfig";
import { fetchLogout } from "../../store/reducers/authentication/apiThunk";
import { Shimmer } from "../../components/CommonComponents/Shimmer";
import { resetBag } from "../../store/reducers/bag/apiThunk";

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add this state for overall loading

  const getProfile = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(fetchProfile());
      const { data, message, status } = response?.payload;
      if (status) {
        setProfileData(data[0]);
      } else {
        ToastNotifyError(message);
      }
    } catch {
      ToastNotifyError("Failed to fetch profile");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  // Handle image load separately from profile data loading
  const handleImageLoad = () => {
    setImgLoaded(true);
  };

  // Reset image loaded state when profile data changes
  useEffect(() => {
    if (profileData?.profile_picture) {
      setImgLoaded(false);
      // Preload the image
      const img = new Image();
      img.src = profileData.profile_picture;
      img.onload = handleImageLoad;
      img.onerror = handleImageLoad; // Also handle errors
    }
  }, [profileData?.profile_picture]);

  const formatSizeOption = (item) => {
    const SizeKey = SIZE_OPTIONS.find((size) => size.key === item);
    return SizeKey?.value || "";
  };
  return (
    <StyledUserProfile>
      {isLoading ? (
        <Box>
          <Shimmer />
        </Box>
      ) : (
        <Box className='user-profile-container'>
          <Box className='user-profile-header-section'></Box>
          <Box className='user-profile-body-section-wrap'>
            <Box className='user-profile-body-section'>
              <Typography className='user-profile-heading'>Profile</Typography>
              <Box className='user-profile-user-info'>
                <Box className='user-profile-img'>
                  {!imgLoaded && profileData?.profile_picture && (
                    <Box className='user-profile-img-loader'>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <img
                    src={profileData?.profile_picture}
                    alt='profile'
                    className='user-img'
                    style={{
                      opacity: imgLoaded ? 1 : 0,
                      transition: "opacity 0.3s ease-in-out",
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageLoad}
                  />
                </Box>
                <Box className='user-profile-info'>
                  <Box className='user-profile-name-container'>
                    <Typography className='user-profile-name'>
                      {profileData?.username}
                    </Typography>
                    <EditOutlinedIcon
                      sx={{ fontSize: "20px", cursor: "pointer" }}
                      onClick={() => navigate(ROUTE_PATH.EDIT_PROFILE)}
                    />
                  </Box>
                  <Typography className='user-profile-email'>
                    {profileData?.email}
                  </Typography>
                  <Box className='user-profile-tags'>
                    <Box className='user-profile-tag'>
                      <Typography className='user-profile-tag-text'>
                        Age: {profileData?.age}
                      </Typography>
                    </Box>
                    <Box className='user-profile-tag'>
                      <Typography className='user-profile-tag-text'>
                        Size: {formatSizeOption(profileData?.sizes)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className='user-profile-btn-section'>
                <Box className='user-profile-btn-container' onClick={() => navigate(ROUTE_PATH.SETTINGS)}>
                  <SettingsOutlinedIcon sx={{ fontSize: "24px" }} />
                  <Box className='user-profile-btn-body'>
                    <Typography className='user-profile-btn-text-heading'>
                      Settings
                    </Typography>
                    <Typography className='user-profile-btn-text'>
                      Email recovery, account updates and more
                    </Typography>
                  </Box>
                </Box>
                <Box
                  className='user-profile-btn-container'
                  onClick={() => navigate(ROUTE_PATH.ORDER_HISTORY)}
                >
                  <ReceiptLongOutlinedIcon sx={{ fontSize: "24px" }} />
                  <Box className='user-profile-btn-body'>
                    <Typography className='user-profile-btn-text-heading'>
                      Order history
                    </Typography>
                    <Typography className='user-profile-btn-text'>
                      Track and manage your previous orders
                    </Typography>
                  </Box>
                </Box>
                <Box className='user-profile-btn-container' onClick={() => navigate(ROUTE_PATH.CUSTOMER_SUPPORT)}>
                  <HeadsetMicOutlinedIcon sx={{ fontSize: "24px" }} />
                  <Box className='user-profile-btn-body'>
                    <Typography className='user-profile-btn-text-heading'>
                      Customer support
                    </Typography>
                    <Typography className='user-profile-btn-text'>
                      Raise issues, access help easily
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box className='user-profile-footer-section'>
                <Button
                  variant='outlined'
                  className='user-profile-logout-btn'
                  onClick={() => setOpenLogoutModal(true)}
                >
                  Log out
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Modal
        open={openLogoutModal}
        onClose={() => {
          setOpenLogoutModal(false);
        }}
      >
        <Box className='logout-profile-modal'>
          <Typography className='logout-profile-modal-title'>
            Log out?
          </Typography>
          <Typography className='logout-profile-modal-body'>
            Are you sure you want to log out?
          </Typography>
          <Box className='logout-profile-modal-btns'>
            <Button
              variant='outlined'
              className='logout-profile-modal-cancel-btn'
              onClick={() => setOpenLogoutModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              className='logout-profile-modal-logout-btn'
              onClick={() => {
                dispatch(fetchLogout());
                dispatch(resetBag());
                setOpenLogoutModal(false);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal> 
    </StyledUserProfile>
  );
};

export default UserProfile;
